import { useSelector } from 'react-redux';
import { createContext, useContext, useEffect } from 'react';
import useListingPageUrlState from '../../components/ListingPage/hooks/useListingPageUrlState';
import StateInterface from '../../redux/types';
import { CategoryEntity } from '../../redux/category/types';
import { EventState } from '../../redux/eventsQueue/types';
import { PageType } from '../UrlManager/types';
import { useLogger } from '../../logger/LoggerProvider';
import useRouter from '../useRouter';
import { ONETRUST_CONSENT_READY_EVENT_NAME } from '../../WestwingEventHandler';
import getEventsById from '../../redux/eventsQueue/selectors';
import { BrazeClientInterface } from './BrazeClient';
import { sendCustomEventForSearch, sendCustomEventForPlp, sendCustomEventForCategory } from './brazeEventService';

const BrazeContext = createContext<BrazeClientInterface | null>(null);

export const BrazeProvider = BrazeContext.Provider;

export const useBraze = () => {
    return useContext(BrazeContext);
};

export const useBrazePlpEvent = (category: CategoryEntity | null) => {
    const braze = useBraze();
    const logger = useLogger();
    const [urlState] = useListingPageUrlState();
    const { location } = useRouter();
    const currentPageId = `${location.pathname}${location.search}`;

    const numberOfProducts = useSelector<StateInterface, number>(state => state.products.products.length);
    const isProductsFetching = useSelector<StateInterface, boolean>(
        state => state.products.isFetching || state.products.identifier !== currentPageId
    );

    const isUserLoggedIn = useSelector<StateInterface, boolean>(state => state.appShellLogin.isLoggedIn);
    const eventsQueue = useSelector<StateInterface, EventState>(state => state.eventsQueue);
    const oneTrustConsentReady = getEventsById(eventsQueue, ONETRUST_CONSENT_READY_EVENT_NAME);
    const targetCookies = !oneTrustConsentReady.data || !oneTrustConsentReady.data.targetingCookies;

    useEffect(() => {
        if (targetCookies) {
            return;
        }
        if (!isUserLoggedIn || isProductsFetching) {
            return;
        }
        if (!braze) {
            logger.warn('Braze client was not registered in a provider');
            return;
        }

        switch (urlState.listingPageType) {
            case PageType.ALLPRODUCTS: {
                sendCustomEventForSearch(braze, urlState.search || '', numberOfProducts);
                break;
            }
            case PageType.CATEGORY: {
                if (!category) {
                    logger.warn('Category was not found for a category page');
                    return;
                }
                sendCustomEventForCategory(braze, category);
                break;
            }
            default: {
                sendCustomEventForPlp(braze, urlState);
                break;
            }
        }
    }, [braze, category, isProductsFetching, isUserLoggedIn, logger, numberOfProducts, targetCookies, urlState]);
};
